import React, { useMemo } from 'react';
import { useTheme, ThemeMode, Theme } from '../contexts/ThemeContext';
import ThemeToggleIcon from './icons/ThemeToggleIcon';

const createStyles = (theme: Theme) => ({
  overlay: {
    position: 'fixed' as const,
    inset: 0,
    backgroundColor: theme.colors.modalOverlay,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing.xl - 4, // 20px
    zIndex: 50,
  },
  modalContainer: {
    width: '100%',
    maxWidth: Math.min(400, window.innerWidth - (theme.spacing.xl * 2)),
    borderRadius: theme.radii.xl,
    backgroundColor: theme.colors.modalBackground,
    boxShadow: theme.shadows.modal,
  },
  header: {
    paddingLeft: theme.spacing.xl,
    paddingRight: theme.spacing.xl,
    paddingTop: theme.spacing.lg,
    paddingBottom: theme.spacing.lg,
    borderBottom: `1px solid ${theme.colors.border}`,
  },
  title: {
    fontSize: theme.typography.sizes.xl,
    fontWeight: theme.typography.weights.semiBold,
    color: theme.colors.text,
    textAlign: 'center' as const,
    margin: 0,
  },
  optionsContainer: {
    paddingTop: theme.spacing.sm,
    paddingBottom: theme.spacing.sm,
  },
  option: {
    paddingLeft: theme.spacing.xl,
    paddingRight: theme.spacing.xl,
    paddingTop: theme.spacing.lg,
    paddingBottom: theme.spacing.lg,
    cursor: 'pointer',
    transition: `background-color ${theme.motion.duration.fast}ms ${theme.motion.easing.standard}`,
    background: 'none',
    border: 'none',
    borderBottom: `1px solid ${theme.colors.border}`,
    width: '100%',
    textAlign: 'left' as const,
  },
  optionSelected: {
    backgroundColor: theme.colors.surfaceSecondary,
  },
  optionContent: {
    display: 'flex',
    flexDirection: 'row' as const,
    alignItems: 'center',
  },
  iconContainer: {
    width: theme.sizes.button.height - 4, // 40px
    height: theme.sizes.button.height - 4, // 40px
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: theme.spacing.md,
  },
  systemIcon: {
    display: 'flex',
    flexDirection: 'row' as const,
    alignItems: 'center',
    gap: 2,
  },
  textContainer: {
    flex: 1,
    textAlign: 'left' as const,
  },
  optionLabel: {
    fontSize: theme.typography.sizes.lg,
    fontWeight: theme.typography.weights.medium,
    color: theme.colors.text,
    marginBottom: 2,
    margin: 0,
    textAlign: 'left' as const,
  },
  optionDescription: {
    fontSize: theme.typography.sizes.md,
    color: theme.colors.textSecondary,
    margin: 0,
    textAlign: 'left' as const,
  },
  checkmark: {
    width: theme.spacing.xl,
    height: theme.spacing.xl,
    borderRadius: theme.radii.full,
    backgroundColor: theme.colors.primary,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  checkmarkText: {
    fontSize: theme.typography.sizes.sm,
    fontWeight: theme.typography.weights.bold,
    color: theme.colors.textInverse,
    margin: 0,
  },
  closeButton: {
    margin: theme.spacing.xl,
    paddingTop: theme.spacing.md,
    paddingBottom: theme.spacing.md,
    borderRadius: theme.radii.lg,
    backgroundColor: theme.colors.buttonPrimary,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: 'none',
    cursor: 'pointer',
    transition: `opacity ${theme.motion.duration.fast}ms ${theme.motion.easing.standard}`,
    width: `calc(100% - ${theme.spacing.xl * 2}px)`, // Full width minus margins
  },
  closeButtonHover: {
    backgroundColor: theme.colors.buttonPrimaryHover,
  },
  closeButtonText: {
    fontSize: theme.typography.sizes.lg,
    fontWeight: theme.typography.weights.semiBold,
    color: theme.colors.textInverse,
    margin: 0,
  },
});

interface ThemeToggleModalProps {
  visible: boolean;
  onClose: () => void;
}

const ThemeToggleModal: React.FC<ThemeToggleModalProps> = ({
  visible,
  onClose,
}) => {
  const { theme, themeMode, setThemeMode } = useTheme();
  const styles = useMemo(() => createStyles(theme), [theme]);

  if (!visible) return null;

  const handleThemeSelect = (mode: ThemeMode) => {
    setThemeMode(mode);
    onClose();
  };

  const themeOptions = [
    {
      mode: 'light' as ThemeMode,
      label: 'Light',
      description: 'Always use light theme',
      icon: <ThemeToggleIcon size={theme.sizes.icon.lg} color={theme.colors.text} isDark={false} />,
    },
    {
      mode: 'dark' as ThemeMode,
      label: 'Dark',
      description: 'Always use dark theme',
      icon: <ThemeToggleIcon size={theme.sizes.icon.lg} color={theme.colors.text} isDark={true} />,
    },
    {
      mode: 'system' as ThemeMode,
      label: 'System',
      description: 'Follow system preference',
      icon: (
        <div style={styles.systemIcon}>
          <ThemeToggleIcon size={theme.sizes.icon.sm - 4} color={theme.colors.text} isDark={false} />
          <ThemeToggleIcon size={theme.sizes.icon.sm - 4} color={theme.colors.text} isDark={true} />
        </div>
      ),
    },
  ];

  return (
    <div
      style={styles.overlay}
      onClick={onClose}
    >
      <div
        style={styles.modalContainer}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={styles.header}>
          <h2 style={styles.title}>
            Theme Settings
          </h2>
        </div>

        {/* Theme Options */}
        <div style={styles.optionsContainer}>
          {themeOptions.map((option) => (
            <button
              key={option.mode}
              style={{
                ...styles.option,
                ...(themeMode === option.mode ? styles.optionSelected : {}),
              }}
              onClick={() => handleThemeSelect(option.mode)}
            >
              <div style={styles.optionContent}>
                <div style={styles.iconContainer}>
                  {option.icon}
                </div>
                <div style={styles.textContainer}>
                  <div style={styles.optionLabel}>
                    {option.label}
                  </div>
                  <div style={styles.optionDescription}>
                    {option.description}
                  </div>
                </div>
                {themeMode === option.mode && (
                  <div style={styles.checkmark}>
                    <span style={styles.checkmarkText}>
                      ✓
                    </span>
                  </div>
                )}
              </div>
            </button>
          ))}
        </div>

        {/* Close Button */}
        <button
          style={styles.closeButton}
          onClick={onClose}
          onMouseEnter={(e) => {
            Object.assign(e.currentTarget.style, {
              ...styles.closeButton,
              ...styles.closeButtonHover,
            });
          }}
          onMouseLeave={(e) => {
            Object.assign(e.currentTarget.style, styles.closeButton);
          }}
        >
          <span style={styles.closeButtonText}>
            Done
          </span>
        </button>
      </div>
    </div>
  );
};

export default ThemeToggleModal;
